"use server";

import { auth } from "@/auth";
import {
  buildMdmPersonCreatePayload,
  buildMdmPersonUpdatePayload,
  MdmPersonPayloadValidationError,
} from "@/lib/cat/mdm-build-person-payload";
import {
  buildMdmCompanyCreatePayload,
  buildMdmCompanyUpdatePayload,
} from "@/lib/cat/mdm-build-company-payload";
import { resolveMdmVehicleBrandFromKvps } from "@/lib/cat/mdm-brand-names";
import { extractMdmAccountSourceId } from "@/lib/cat/mdm-map-account";
import { extractMdmSourceId } from "@/lib/cat/mdm-map-company";
import {
  resolveMdmCompanyFromDraft,
  resolveMdmPersonFromDraft,
} from "@/lib/cat/mdm-party-resolution";
import {
  buildCompanySyncPlan,
  buildCompanySyncPlanForCreate,
} from "@/lib/cat/mdm-company-sync-plan";
import {
  buildPersonSyncPlan,
  buildPersonSyncPlanForCreate,
} from "@/lib/cat/mdm-person-sync-plan";
import { canAccessDraft } from "@/lib/cat/draft-access";
import {
  MDM_HEALTH_TECHNICAL_MESSAGE,
  resolveMdmHealthMessage,
  resolveMdmSyncFailureMessage,
} from "@/lib/cat/mdm-health-messages";
import {
  shouldShowCompanyRecap,
  shouldShowIndividualRecap,
} from "@/lib/cat/mdm-recap-from-draft";
import {
  mdmUpsertCompany,
  mdmUpsertPerson,
} from "@/lib/cat/mulesoft-mdm-write";
import { MulesoftRequestError } from "@/lib/mulesoft";
import { prisma } from "@/prisma";
import type {
  MdmClientHealthResult,
  MdmCompanyResolution,
  MdmPartyResolutionStatus,
  MdmPersonResolution,
  MdmSyncOutcome,
  SyncMdmFromDraftResult,
} from "@/types/cat/mdm-party-resolution.types";
import type { Draft } from "@/prisma/generated/client";
import type { Session } from "next-auth";

const KVPS_BRAND_ERROR_MESSAGE =
  "La marque du véhicule est indéterminable pour l'envoi RefPers.";

async function requireDraftAccess(draftId: number): Promise<{
  session: Session;
  draft: Draft | null;
}> {
  const session = await auth();
  if (!session?.user?.id) {
    throw new Error("Unauthenticated MDM sync request");
  }

  const draft = await prisma.draft.findUnique({ where: { id: draftId } });
  if (!draft) {
    return { session, draft: null };
  }

  if (!canAccessDraft(session, draft)) {
    throw new Error("Access denied to draft for MDM sync");
  }

  return { session, draft };
}

function buildHealthFailureResult(
  status: MdmPartyResolutionStatus,
  extra?: Omit<MdmClientHealthResult, "status" | "message" | "errorKind">,
): MdmClientHealthResult {
  const { message, errorKind } = resolveMdmHealthMessage(status);
  return { status, message, errorKind, ...extra };
}

function resolveVehicleBrand(draft: Draft) {
  const brand = resolveMdmVehicleBrandFromKvps(draft.kvps);
  if (!brand) {
    throw new MdmPersonPayloadValidationError(
      "undetermined KVPS brand for MDM sync",
      KVPS_BRAND_ERROR_MESSAGE,
    );
  }
  return brand;
}

async function persistAutoRematchedClientId(
  draftId: number,
  clientId: string,
): Promise<Draft> {
  return prisma.draft.update({
    where: { id: draftId },
    data: { clientId },
  });
}

async function applyPersonResolution(
  draft: Draft,
  resolution: MdmPersonResolution,
): Promise<{ draft: Draft; resolution: MdmPersonResolution }> {
  if (!resolution.autoRematched || !resolution.resolvedClientId) {
    return { draft, resolution };
  }

  const updatedDraft = await persistAutoRematchedClientId(
    draft.id,
    resolution.resolvedClientId,
  );
  return { draft: updatedDraft, resolution };
}

function failureFromPersonResolution(
  resolution: MdmPersonResolution,
): SyncMdmFromDraftResult {
  const code =
    resolution.status === "search_error"
      ? "search_error"
      : resolution.status === "invalid_criteria"
        ? "invalid_criteria"
        : resolution.status === "not_found"
          ? "not_found"
          : "id_stale";

  return {
    success: false,
    code,
    message: resolveMdmSyncFailureMessage(code),
  };
}

function failureFromCompanyResolution(
  resolution: MdmCompanyResolution,
): SyncMdmFromDraftResult {
  const code =
    resolution.status === "search_error"
      ? "search_error"
      : resolution.status === "invalid_criteria"
        ? "invalid_criteria"
        : resolution.status === "not_found"
          ? "not_found"
          : "id_stale";

  return {
    success: false,
    code,
    message: resolveMdmSyncFailureMessage(code),
  };
}

function combineMdmOutcomes(
  outcomes: Array<MdmSyncOutcome | undefined>,
): MdmSyncOutcome {
  const applicable = outcomes.filter(
    (o): o is MdmSyncOutcome => o !== undefined && o !== "not_applicable",
  );
  if (applicable.length === 0) return "not_applicable";
  if (applicable.some((o) => o === "synced")) return "synced";
  return "skipped";
}

async function executePersonMdmWrite(
  draft: Draft,
  plan: ReturnType<typeof buildPersonSyncPlan>,
  brand: ReturnType<typeof resolveMdmVehicleBrandFromKvps> & object,
): Promise<{ clientId?: string; mdmOutcome: MdmSyncOutcome }> {
  const payloadInput = {
    draft,
    mdmBrandCode: brand.mdmBrandCode,
    mdmBrandRelationName: brand.brandRelationName,
    includeConsent: plan.includeConsent,
  };

  if (plan.mode === "create") {
    const payload = buildMdmPersonCreatePayload(payloadInput);
    const response = await mdmUpsertPerson(payload);
    const clientId = extractMdmAccountSourceId(response);
    if (!clientId || clientId === "mdm-unknown") {
      throw new MdmPersonPayloadValidationError(
        "missing person MDM source id in upsert response",
        "Réponse RefPers client invalide : identifiant MDM manquant.",
      );
    }
    await prisma.draft.update({
      where: { id: draft.id },
      data: { clientId },
    });
    return { clientId, mdmOutcome: "synced" };
  }

  if (plan.mode === "update") {
    const clientId = draft.clientId?.trim();
    if (!clientId) {
      throw new MdmPersonPayloadValidationError(
        "missing clientId for update plan",
        "Identifiant client RefPers manquant pour la mise à jour.",
      );
    }
    const payload = buildMdmPersonUpdatePayload({
      ...payloadInput,
      clientId,
      patchAddress: plan.patchAddress,
    });
    await mdmUpsertPerson(payload);
    return { clientId, mdmOutcome: "synced" };
  }

  return { clientId: draft.clientId ?? undefined, mdmOutcome: "skipped" };
}

async function executeCompanyMdmWrite(
  draft: Draft,
  plan: ReturnType<typeof buildCompanySyncPlan>,
  brand: ReturnType<typeof resolveMdmVehicleBrandFromKvps> & object,
): Promise<{ companyMdmSourceId?: string; mdmOutcome: MdmSyncOutcome }> {
  const payloadInput = {
    draft,
    mdmBrandCode: brand.mdmBrandCode,
    mdmBrandRelationName: brand.brandRelationName,
  };

  if (plan.mode === "create") {
    const payload = buildMdmCompanyCreatePayload(payloadInput);
    const response = await mdmUpsertCompany(payload);
    const companyMdmSourceId = extractMdmSourceId(response);
    if (!companyMdmSourceId) {
      throw new MdmPersonPayloadValidationError(
        "missing company MDM source id in upsert response",
        "Réponse RefPers société invalide : identifiant MDM manquant.",
      );
    }
    await prisma.draft.update({
      where: { id: draft.id },
      data: {
        companyMdmSourceId,
        companyRegisteredInMdm: true,
      },
    });
    return { companyMdmSourceId, mdmOutcome: "synced" };
  }

  if (plan.mode === "update") {
    const companyId = draft.companyMdmSourceId?.trim();
    if (!companyId) {
      throw new MdmPersonPayloadValidationError(
        "missing companyMdmSourceId for update plan",
        "Identifiant société RefPers manquant pour la mise à jour.",
      );
    }
    const payload = buildMdmCompanyUpdatePayload({
      ...payloadInput,
      companyId,
    });
    await mdmUpsertCompany(payload);
    return { companyMdmSourceId: companyId, mdmOutcome: "synced" };
  }

  return {
    companyMdmSourceId: draft.companyMdmSourceId ?? undefined,
    mdmOutcome: "skipped",
  };
}

async function syncPersonMdmFromDraft(
  draft: Draft,
  brand: ReturnType<typeof resolveMdmVehicleBrandFromKvps> & object,
): Promise<
  | {
      success: true;
      draft: Draft;
      clientId?: string;
      mdmOutcome: MdmSyncOutcome;
    }
  | { success: false; result: SyncMdmFromDraftResult }
> {
  let workingDraft = draft;
  const storedClientId = workingDraft.clientId?.trim() ?? "";

  let plan;
  if (!storedClientId) {
    plan = buildPersonSyncPlanForCreate(workingDraft);
  } else {
    let resolution = await resolveMdmPersonFromDraft(workingDraft);
    const applied = await applyPersonResolution(workingDraft, resolution);
    workingDraft = applied.draft;
    resolution = applied.resolution;

    if (resolution.status !== "matched") {
      return {
        success: false,
        result: failureFromPersonResolution(resolution),
      };
    }

    plan = buildPersonSyncPlan({ draft: workingDraft, resolution });
  }

  const result = await executePersonMdmWrite(workingDraft, plan, brand);
  return {
    success: true,
    draft: workingDraft,
    clientId: result.clientId ?? workingDraft.clientId ?? undefined,
    mdmOutcome: result.mdmOutcome,
  };
}

async function syncCompanyMdmFromDraft(
  draft: Draft,
  brand: ReturnType<typeof resolveMdmVehicleBrandFromKvps> & object,
): Promise<
  | {
      success: true;
      draft: Draft;
      companyMdmSourceId?: string;
      mdmOutcome: MdmSyncOutcome;
    }
  | { success: false; result: SyncMdmFromDraftResult }
> {
  const storedCompanyId = draft.companyMdmSourceId?.trim() ?? "";

  let plan;
  if (!storedCompanyId) {
    plan = buildCompanySyncPlanForCreate(draft);
  } else {
    const resolution = await resolveMdmCompanyFromDraft(draft, {
      mdmBrandCode: brand.mdmBrandCode,
    });

    if (resolution.status !== "matched") {
      return {
        success: false,
        result: failureFromCompanyResolution(resolution),
      };
    }

    plan = buildCompanySyncPlan({ draft, resolution });
  }

  const result = await executeCompanyMdmWrite(draft, plan, brand);
  return {
    success: true,
    draft,
    companyMdmSourceId:
      result.companyMdmSourceId ?? draft.companyMdmSourceId ?? undefined,
    mdmOutcome: result.mdmOutcome,
  };
}

export async function checkMdmClientHealth({
  draftId,
}: {
  draftId: number;
}): Promise<MdmClientHealthResult> {
  try {
    const { draft } = await requireDraftAccess(draftId);
    if (!draft) {
      return { status: "matched" };
    }

    const needCompany = shouldShowCompanyRecap(draft);
    const needPerson = shouldShowIndividualRecap(draft);

    if (!needCompany && !needPerson) {
      return { status: "matched" };
    }

    if (needCompany && draft.companyMdmSourceId?.trim()) {
      const companyResolution = await resolveMdmCompanyFromDraft(draft);
      if (companyResolution.status !== "matched") {
        return buildHealthFailureResult(companyResolution.status, {
          party: "company",
          companyMdmSourceId: draft.companyMdmSourceId,
        });
      }
    }

    if (needPerson && draft.clientId?.trim()) {
      let resolution = await resolveMdmPersonFromDraft(draft);
      const applied = await applyPersonResolution(draft, resolution);
      resolution = applied.resolution;

      if (resolution.status !== "matched") {
        return buildHealthFailureResult(resolution.status, {
          party: "person",
          clientId: draft.clientId,
          autoRematched: resolution.autoRematched,
        });
      }

      return {
        status: "matched",
        clientId: resolution.resolvedClientId ?? draft.clientId,
        companyMdmSourceId: draft.companyMdmSourceId ?? undefined,
        autoRematched: resolution.autoRematched,
      };
    }

    return {
      status: "matched",
      clientId: draft.clientId ?? undefined,
      companyMdmSourceId: draft.companyMdmSourceId ?? undefined,
    };
  } catch (error) {
    console.error("[checkMdmClientHealth]", error);
    return buildHealthFailureResult("search_error");
  }
}

export async function syncMdmFromDraft({
  draftId,
}: {
  draftId: number;
}): Promise<SyncMdmFromDraftResult> {
  try {
    const { draft: accessibleDraft } = await requireDraftAccess(draftId);
    let draft = accessibleDraft;
    if (!draft) {
      return {
        success: false,
        message: "Brouillon introuvable.",
        code: "mdm_error",
      };
    }

    const needCompany = shouldShowCompanyRecap(draft);
    const needPerson = shouldShowIndividualRecap(draft);

    if (!needCompany && !needPerson) {
      return { success: true, mdmOutcome: "not_applicable" };
    }

    const brand = resolveVehicleBrand(draft);
    let clientId = draft.clientId ?? undefined;
    let companyMdmSourceId = draft.companyMdmSourceId ?? undefined;
    const outcomes: Array<MdmSyncOutcome | undefined> = [];

    if (needCompany) {
      const companyResult = await syncCompanyMdmFromDraft(draft, brand);
      if (!companyResult.success) {
        return companyResult.result;
      }
      draft = companyResult.draft;
      companyMdmSourceId = companyResult.companyMdmSourceId;
      outcomes.push(companyResult.mdmOutcome);
    }

    if (needPerson) {
      const personResult = await syncPersonMdmFromDraft(draft, brand);
      if (!personResult.success) {
        return personResult.result;
      }
      draft = personResult.draft;
      clientId = personResult.clientId;
      outcomes.push(personResult.mdmOutcome);
    }

    return {
      success: true,
      clientId,
      companyMdmSourceId,
      mdmOutcome: combineMdmOutcomes(outcomes),
    };
  } catch (error) {
    console.error("[syncMdmFromDraft]", error);

    if (error instanceof MdmPersonPayloadValidationError) {
      return {
        success: false,
        message: resolveMdmSyncFailureMessage("mdm_error"),
        code: "mdm_error",
      };
    }

    if (error instanceof MulesoftRequestError) {
      return {
        success: false,
        message: resolveMdmSyncFailureMessage("mdm_error"),
        code: "mdm_error",
      };
    }

    return {
      success: false,
      message: MDM_HEALTH_TECHNICAL_MESSAGE,
      code: "mdm_error",
    };
  }
}
